import { fromNullable, Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { findSelfLink } from '../../../LinkyLinky';
import ProgramStore from '../../../ProgramStore';
import {
  ActiveProgramModule,
  ProgramSegment,
  whenActive,
  whenUpcoming,
} from '../../../ProgramStore/Types';
import SegmentStore from '../../../SegmentStore';
import { windowScrollToElement } from '../../../Window';
import OutlineLegend from '../OutlineLegend';
import OutlineModules from '../OutlineModules';
import UpcomingOutlineModules from '../UpcomingOutlineModules';
import * as style from '../style.module.css';
import ProgramOverviewTitle from './ProgramOverviewTitle';

interface Props {
  programStore: ProgramStore;
  segmentStore: SegmentStore;
}

const latestAvailableSegmentId = (modules: ActiveProgramModule[]): Maybe<number> =>
  fromNullable(
    modules
      .flatMap((m) => m.segments)
      .find((segment) => segment.status === 'Accessible' || segment.status === 'Started'),
  ).map((segment) => segment.id);

const OutlineTabPane: React.FC<Props> = ({ programStore, segmentStore }) => {
  const currentSegmentId = segmentStore.segmentResource.map((resource) => resource.payload.id);
  const viewedSegmentReady = segmentStore.nextAdvanceActive;

  // Jump to a segment via the store (the only navigation the in-course view honors),
  // then reload the course map so module %, the current-segment marker and the progress
  // bar stay in sync — the same refresh Next/Previous trigger via the 'advancing-to' state.
  const onNavigate = (segment: ProgramSegment): void => {
    // Clicking the segment you're already on must not reload it — that would reset the
    // store's form-field tracking and briefly flash the content. Just re-align to the top.
    const alreadyViewing = currentSegmentId.map((id) => id === segment.id).getOrElseValue(false);
    if (alreadyViewing) {
      // Already here — just frame the segment content at the top of the viewport.
      windowScrollToElement('segment-column').fork(
        () => {},
        () => {},
      );
      return;
    }
    findSelfLink(segment.links).do((link) => {
      segmentStore.navigateToSegment(link);
      programStore.programDetailResource.map(programStore.reloading);
      // The frame-scroll is handled by ProgramSegmentReactions on the 'loaded' state, once
      // the new segment content has actually rendered.
    });
  };

  return programStore.programDetailResource
    .map((pdr) => {
      return (
        <div
          className={`${style.tabContent} ${style.outlineUnbounded}`}
          data-test-outline-tab-pane={true}
          role="region"
          aria-label="Program outline"
          tabIndex={0}
        >
          <div className={style.outlineHeader}>
            <ProgramOverviewTitle programDetailResource={pdr} />
            <OutlineLegend />
          </div>
          <div className={style.outline}>
            {whenActive(pdr.payload)
              .map((active) => active.modules)
              .map<React.ReactNode>((modules) => (
                <OutlineModules
                  modules={modules}
                  currentSegmentId={currentSegmentId}
                  viewedSegmentReady={viewedSegmentReady}
                  onNavigate={onNavigate}
                  latestAvailableSegmentId={latestAvailableSegmentId(modules)}
                />
              ))
              .orElse(() =>
                whenUpcoming(pdr.payload).map<React.ReactNode>((upcoming) => (
                  <UpcomingOutlineModules modules={upcoming.modules} />
                )),
              )
              .getOrElseValue(<span />)}
          </div>
        </div>
      );
    })
    .getOrElseValue(<span />);
};
export default observer(OutlineTabPane);
